// ServiceOptionBadges — renders an owned item's `service_options` (TD-671
// Task 13, operator ruling R9). The server decides which options apply; this
// component only presents them:
//
//   - `app_download`  -> a text badge that links to the store listing
//                        ("Download on the App Store" / "Get it on Google
//                        Play"). The wire's `icon` is a platform KEY, not an
//                        image URL: the embed's CSP blocks external image
//                        hosts, so the badge is text plus an inline SVG glyph.
//   - `auto_installed`-> the server's `message` verbatim (copy is owned by the
//                        server so every client says the same thing).
//
// Defence in depth: only an absolute `https:` URL is rendered as a link. The
// server already validates the URL, but a link built from a string that
// arrives over the wire is exactly where a `javascript:` scheme would hide, so
// the client refuses everything else instead of trusting upstream. A refused
// option renders nothing (no dead badge).
//
// Clients MUST ignore an unknown `type` (the spec reserves `serial_numbers`
// and `renew`): the switch has no default arm that renders. A `help` ACTION is
// not a service option and is never rendered anywhere in the embed (R13).
//
// Inline styles only — no Tailwind.

import { useEmbedTokens, EmbedText } from "../../embed-shared/primitives";
import type { ServiceOptionWire } from "./owned-products-api";

type AppDownload = Extract<ServiceOptionWire, { type: "app_download" }>;

const BADGE_LABEL: Record<AppDownload["icon"], string> = {
  app_store: "Download on the App Store",
  google_play: "Get it on Google Play",
};

function isHttpsUrl(raw: string): boolean {
  try {
    return new URL(raw).protocol === "https:";
  } catch {
    return false;
  }
}

function DownloadGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ flex: "0 0 auto" }}>
      <path
        d="M8 1.5v8m0 0L4.5 6M8 9.5 11.5 6M2.5 13h11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AppBadge({ option }: { option: AppDownload }) {
  const t = useEmbedTokens();
  const label = BADGE_LABEL[option.icon];
  if (!label || !isHttpsUrl(option.url)) return null;
  return (
    <a
      href={option.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`app-badge-${option.platform}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        boxSizing: "border-box",
        padding: "2px 10px",
        minHeight: t.pointer === "coarse" ? t.size.touchTarget : 28,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.borderRadiusSm,
        color: t.colors.text,
        fontFamily: "inherit",
        fontSize: t.fontSize.xs,
        fontWeight: t.fontWeight.regular,
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      <DownloadGlyph />
      {label}
    </a>
  );
}

export function ServiceOptionBadges({ options }: { options: ServiceOptionWire[] | null | undefined }) {
  const t = useEmbedTokens();
  const rendered = (options ?? []).flatMap((option, i) => {
    switch (option.type) {
      case "app_download":
        return isHttpsUrl(option.url) && BADGE_LABEL[option.icon]
          ? [<AppBadge key={`${option.type}-${option.platform}-${i}`} option={option} />]
          : [];
      case "auto_installed":
        return option.message
          ? [
              <EmbedText key={`${option.type}-${i}`} tone="secondary" size="xs">
                <span data-testid="service-option-auto-installed">{option.message}</span>
              </EmbedText>,
            ]
          : [];
      default:
        // Unknown variant (serial_numbers, renew, ...): ignored by contract.
        return [];
    }
  });
  if (rendered.length === 0) return null;
  return (
    <div
      data-testid="service-options"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: t.spacing.xs }}
    >
      {rendered}
    </div>
  );
}
